import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import EntitiesPage from "@/pages/Entities";
import InvoicesPage from "@/pages/Invoices";
import TeamPage from "@/pages/Team";
import PortalPage from "@/pages/Portal";
import ResetPasswordPage from "@/pages/ResetPassword";
import AuditLogPage from "@/pages/AuditLog";
import LoginAuditPage from "@/pages/LoginAudit";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import { PageSpinner } from "@/components/Spinner";

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) {
    return <PageSpinner />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  // Detect Emergent OAuth callback synchronously during render (before ProtectedRoute
  // and AuthContext can race). Must read from useLocation().hash, NOT window.location.hash.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      {/* Portal is served under `/s/:token` (short URL) and the legacy
          `/portal/:token` alias — both public, no auth. */}
      <Route path="/s/:token" element={<PortalPage />} />
      <Route path="/portal/:token" element={<PortalPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route
        element={
          <Protected>
            <AppLayout>
              <Outlet />
            </AppLayout>
          </Protected>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/clients" element={<EntitiesPage type="client" />} />
        <Route path="/partners" element={<EntitiesPage type="partner" />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route
          path="/team"
          element={
            <Protected adminOnly>
              <TeamPage />
            </Protected>
          }
        />
        <Route
          path="/audit"
          element={
            <Protected adminOnly>
              <AuditLogPage />
            </Protected>
          }
        />
        <Route
          path="/audit/logins"
          element={
            <Protected adminOnly>
              <LoginAuditPage />
            </Protected>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
